import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Shield, Phone, Mail, User, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { pharmacyContent, clinicInfo, pharmacist } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pharmacy = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Pharmacy',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const payload = {
      page: "Contact", // change to "Pharmacy" on Pharmacy page, etc.
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      submittedAt: new Date().toISOString(),
    };

    const res = await fetch("https://formspree.io/f/mvzagdog", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Form submission failed");

    alert("Thanks! Your message has been sent.");
    setFormData({ name: "", email: "", phone: "", message: "" });
  } catch (err) {
    console.error(err);
    alert("Sorry — something went wrong. Please try again or call the clinic.");
  } finally {
    setIsSubmitting(false);
  }
};


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#000053] mb-6">
              {pharmacyContent.title}
            </h1>
            <p className="text-xl text-[#36454F] mb-8">
              {pharmacyContent.subtitle}
            </p>
            <Link to="/book">
              <Button size="lg" className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all">
                Contact Our Pharmacy
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-12">
              {pharmacyContent.description}
            </p>

            {/* Services Grid */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-[#000053] mb-8 text-center">Pharmacy Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pharmacyContent.services.map((service, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-[#006D73] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Pharmacist Info */}
            <Card className="border-2 border-[#006D73]">
              <CardContent className="p-8">
                <div className="flex items-start space-x-6">
                  <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 border-4 border-[#006D73] border-opacity-20">
                    <img 
                      src={pharmacist.photo}
                      alt={pharmacist.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#000053] mb-2">{pharmacist.name}</h3>
                    <p className="text-[#006D73] font-semibold mb-3">{pharmacist.role} - {pharmacist.credentials}</p>
                    <p className="text-gray-700 leading-relaxed">
                      {pharmacist.bio}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#006D73]">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <Shield className="h-8 w-8 text-[#006D73] flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-[#000053] mb-3">Insurance & Payment</h3>
                    <p className="text-gray-700">{pharmacyContent.insurance}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pharmacy Contact */}
            <Card className="border-2 border-[#006D73]">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#000053] mb-6">Pharmacy Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <Phone className="h-6 w-6 text-[#006D73] flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Phone</p>
                      <a href={`tel:${clinicInfo.pharmacyPhone}`} className="text-[#006D73] hover:underline text-lg font-semibold">
                        {clinicInfo.pharmacyPhone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Mail className="h-6 w-6 text-[#006D73] flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Email</p>
                      <a href={`mailto:${clinicInfo.pharmacyEmail}`} className="text-[#006D73] hover:underline text-lg font-semibold">
                        {clinicInfo.pharmacyEmail}
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card className="border-2 border-[#006D73]">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#000053] mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#006D73] text-white hover:bg-[#005a5f]"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#000053]">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Experience Convenient Pharmacy Care
            </h2>
            <p className="text-gray-300 mb-8">
              Our knowledgeable pharmacists are here to help with all your medication needs.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all">
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pharmacy;